import LogRocket from 'logrocket';
import { LiliPlanType } from '@lili/lili-frontend-common';
import { EnvironmentVariables, getEnvironmentVariable } from '@Services/index';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';
import { useBoundStore } from '@Store/useBoundStore';
import { getFlowId } from '@Services/analytics/analytics.service';
import { version } from '../../../package.json';

import type { OnboardingStatus, OnboardingTypeEnum } from '@Store/slices/customer/customer.types';
import type { AuthorizedUserType } from '@Store/slices/authOnboarding/authOnboarding.types';
import type { MAIN_INCOME_SOURCE, PartnerFlowIds } from '@Types/consts';

export interface LogRocketUserData {
  isReactProject: true;
  flowId: PartnerFlowIds;
  entityType?: MAIN_INCOME_SOURCE;
  isStar?: boolean;
  isWhiteGlove?: boolean;
  onboardingTypeEnum?: OnboardingTypeEnum;
  isFromLiliApp?: boolean;
  isPartnerFlow?: boolean;
  isReturningUser?: boolean;
  isMultiBusiness?: boolean;
  isUsFlow?: boolean;
  isNonUsSupported?: boolean;
  hasMfa?: boolean;
  onboardingStatus?: OnboardingStatus;
  canSelectPlan?: true;
  defaultPlanSelected?: LiliPlanType.SMART;
  primaryCustomerId?: string;
  authorizedUserType?: AuthorizedUserType;
  campaignName?: string;
}

export const initLogRocket = () => {
  LogRocket.init(getEnvironmentVariable(EnvironmentVariables.VITE_APP_LOGROCKET), {
    release: version,
    network: {
      requestSanitizer: (request) => {
        if (request.headers['Authorization']) {
          request.headers['Authorization'] = '***';
        }
        if (request.headers['authorization']) {
          request.headers['authorization'] = '***';
        }
        if (request.body) {
          try {
            const body = JSON.parse(request.body);
            if (body.password) body.password = '***';
            if (body.ssn) body.ssn = '***';
            request.body = JSON.stringify(body);
          } catch {
            // Not JSON, leave as-is
          }
        }
        return request;
      },
      responseSanitizer: (response) => {
        if (response.body) {
          try {
            const body = JSON.parse(response.body);
            if (body.accessToken) body.accessToken = '***';
            if (body.stateToken) body.stateToken = '***';
            response.body = JSON.stringify(body);
          } catch {
            // Not JSON, leave as-is
          }
        }
        return response;
      },
    },
  });
};

const setLogRocketUserData = (
  customerId: string,
  data: Record<string, string | number | boolean> = {}
) => {
  try {
    LogRocket.identify(customerId, data);
  } catch (error) {
    reportError({
      metadata: {
        name: 'LogRocketIdentifyError',
        message: `Failed to identify customer ${customerId}: ${JSON.stringify(error)}`,
      },
      severity: ObservabilityErrorSeverity.WARNING,
      groupingHash: 'logrocket-identify-error',
    });
  }
};

export const buildLogRocketData = (overrides: Partial<LogRocketUserData> = {}) => {
  const { onboardingSlice, customer, isUsFlow } = useBoundStore.getState();

  const baseData = {
    isReactProject: true,
    canSelectPlan: true,
    defaultPlanSelected: LiliPlanType.SMART,
    flowId: getFlowId(),
    entityType: customer.customerData?.customerBusinessData?.mainIncomeSource,
    isStar: customer.isStar ?? false,
    isWhiteGlove: onboardingSlice.getIsWhiteGloveFlow(),
    isFromLiliApp: onboardingSlice.isFromLiliApp,
    isPartnerFlow: onboardingSlice.isPartnerFlow,
    onboardingStatus: customer.onboardingStatus,
    onboardingTypeEnum: customer.customerData?.onboardingTypeEnum,
    isNonUsSupported: customer.isNonUsSupported,
    campaignName: customer.campaignName,
    isUsFlow,
  } satisfies LogRocketUserData;

  const mergedData = { ...baseData, ...overrides };
  const filteredEntries = Object.entries(mergedData).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );

  return Object.fromEntries(filteredEntries) as unknown as LogRocketUserData;
};

export const identifyLogRocketUser = (
  customerId: string,
  overrides: Partial<LogRocketUserData> = {}
) => {
  const data = buildLogRocketData(overrides);
  setLogRocketUserData(customerId, data as unknown as Record<string, string | number | boolean>);
};

type TrackEventProperties = Record<
  string,
  string | number | boolean | string[] | number[] | boolean[] | null | undefined
>;

export const trackLogRocketEvent = (eventName: string, properties?: TrackEventProperties) => {
  try {
    LogRocket.track(eventName, properties);
  } catch {
    // Silently fail — non-critical
  }
};

export const captureLogRocketException = (error: Error) => {
  try {
    LogRocket.captureException(error);
  } catch {
    // Silently fail — non-critical
  }
};

export const getLogRocketSessionURL = (callback: (sessionURL: string) => void) => {
  LogRocket.getSessionURL(callback);
};
